import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScrollText, Swords, Shield, BookOpen, Hourglass, Crown, Lock, X, ArrowRight } from 'lucide-react';
import { Tracker } from '../../screens/Tracker';
import { ResourceGrid } from './ResourceGrid';
import { ResourcesBook } from './SpellBook';
import { Library, type ShelfBook } from './Library';
import { ReadinessBook } from './ReadinessBook';
import { PrescriptionBook } from './PrescriptionBook';
import { MarathonView } from '../ledger/Marathon';
import { useAppState } from '../../hooks/useAppState';
import { authApi, strategiesApi, tradesApi, engineApi } from '../../lib/api';
import { PrescriptionEngine } from '../ledger/PrescriptionEngine';
import { SwapWord } from './SwapWord';
import { useSkin } from '../../hooks/useSkin';
import { ICE, TEAL, ACCENT, FIELD, PANEL_GLASS, PX_FONT, TITLE_FONT, pixelFrame } from './theme';

/* Home Base sections as full-width rows. A row slides open in place to show its section;
   closing it folds it back into the list. The open row is kept in ?open= so back/refresh work. */

interface Section {
  key: string;
  title: [string, string];
  blurb: string;
  icon: ReactNode;
  art: ReactNode;
  to?: string;
  locked?: string;
  innerCircle?: boolean;
}

/** Binding colour for each area's book on the library shelf */
const BINDING: Record<string, string> = { resources: 'red', readiness: 'blue', rx: 'green', tracker: 'purple', marathon: 'brown', mentor: 'grey' }; // leathers from the pixel kit

/** Short titles that fit a spine */
const SPINE: Record<string, string> = { resources: 'Resources', readiness: 'Readiness', rx: 'Prescription', tracker: 'Live Trading', marathon: 'Marathon', mentor: 'Mentor' };

const glow = { filter: 'drop-shadow(0 0 4px rgba(52,245,197,0.55))' };
const A = 'var(--pal-accent)';
const DIM = 'var(--pal-muted)';
const LOSS = 'var(--pal-loss)';

/** Risk gauge: the prescription engine dials your risk in */
const GaugeArt = (
  <svg viewBox="0 0 280 96" width="100%" height="100%" style={glow}>
    {Array.from({ length: 13 }, (_, i) => {
      const a = Math.PI * (1 - i / 12);
      const r1 = 54, r2 = i % 3 === 0 ? 42 : 47;
      return <line key={i} x1={140 + r1 * Math.cos(a)} y1={88 - r1 * Math.sin(a)} x2={140 + r2 * Math.cos(a)} y2={88 - r2 * Math.sin(a)} stroke={i > 9 ? LOSS : A} strokeWidth={i % 3 === 0 ? 2.5 : 1.5} />;
    })}
    <path d="M 78 88 A 62 62 0 0 1 202 88" fill="none" stroke={A} strokeOpacity={0.35} strokeWidth={2} />
    <line x1={140} y1={88} x2={140 + 44 * Math.cos(Math.PI * 0.62)} y2={88 - 44 * Math.sin(Math.PI * 0.62)} stroke={ICE} strokeWidth={3} strokeLinecap="round" className="pal-needle" />
    <circle cx={140} cy={88} r={5} fill={A} />
    <text x={226} y={60} fill={A} fontFamily="Inter, sans-serif" fontWeight={800} fontSize={22}>0.5R</text>
    <text x={226} y={78} fill={DIM} fontFamily="Inter, sans-serif" fontSize={10}>per trade</text>
  </svg>
);

/** Candles with an entry and stop: the trade log */
const CANDLES: [number, number, number, number][] = [[52, 60, 48, 66], [58, 50, 44, 62], [50, 56, 46, 60], [56, 42, 38, 58], [44, 36, 30, 48], [36, 40, 30, 44], [40, 28, 24, 44], [28, 22, 16, 32], [22, 26, 18, 30], [26, 14, 10, 28]];
const CandleArt = (
  <svg viewBox="0 0 280 96" width="100%" height="100%" style={glow}>
    <line x1={20} x2={268} y1={56} y2={56} stroke={A} strokeDasharray="4 4" strokeOpacity={0.6} />
    <line x1={20} x2={268} y1={72} y2={72} stroke={LOSS} strokeDasharray="4 4" strokeOpacity={0.7} />
    {CANDLES.map(([o, c, hi, lo], i) => {
      const x = 34 + i * 24, up = c < o;
      return (
        <g key={i} className={i === CANDLES.length - 1 ? 'pal-pulse' : undefined}>
          <line x1={x} x2={x} y1={hi} y2={lo} stroke={up ? A : LOSS} strokeWidth={1.5} />
          <rect x={x - 5} y={Math.min(o, c)} width={10} height={Math.max(2, Math.abs(o - c))} fill={up ? A : LOSS} />
        </g>
      );
    })}
  </svg>
);

/** Replay → paper → live: the readiness ladder filling up */
const LadderArt = (
  <svg viewBox="0 0 280 96" width="100%" height="100%" style={glow}>
    {[['Replay', 1], ['Paper', 0.62], ['Live', 0]].map(([name, p], i) => (
      <g key={name as string} transform={`translate(30 ${16 + i * 26})`}>
        <text x={0} y={10} fill={DIM} fontFamily="Inter, sans-serif" fontSize={10}>{name}</text>
        <rect x={50} y={2} width={180} height={9} rx={4} fill="none" stroke={A} strokeOpacity={0.35} />
        <rect x={50} y={2} width={180 * (p as number)} height={9} rx={4} fill={A} className={i === 1 ? 'pal-pulse' : undefined} />
        {i === 2 && <path d="M 236 1 h8 v4 h-2 v-2 h-4 z M 235 5 h10 v8 h-10 z" fill={DIM} />}
      </g>
    ))}
  </svg>
);

/** Master feeding four subs: the Marathon rotation */
const RotationArt = (
  <svg viewBox="0 0 280 96" width="100%" height="100%" style={glow}>
    <path d="M140 30 V44 M62 44 H218 M62 44 V58 M114 44 V58 M166 44 V58 M218 44 V58" fill="none" stroke={A} strokeOpacity={0.5} strokeWidth={1.5} />
    <rect x={118} y={8} width={44} height={22} fill={A} />
    <text x={140} y={23} textAnchor="middle" fill="var(--pal-on-accent)" fontFamily="Inter, sans-serif" fontWeight={800} fontSize={11}>MA</text>
    {['sA1', 'sA2', 'sA3', 'sA4'].map((l, i) => (
      <g key={l} className={i === 1 ? 'pal-pulse' : undefined}>
        <rect x={44 + i * 52} y={58} width={36} height={22} fill="none" stroke={A} strokeWidth={1.5} strokeDasharray={i > 1 ? '4 3' : undefined} />
        <text x={62 + i * 52} y={73} textAnchor="middle" fill={A} fontFamily="Inter, sans-serif" fontWeight={700} fontSize={10}>{l}</text>
      </g>
    ))}
  </svg>
);

/** Stacked playbook pages: resources */
const PagesArt = (
  <svg viewBox="0 0 280 96" width="100%" height="100%" style={glow}>
    {[0, 1, 2].map(i => (
      <g key={i} transform={`translate(${96 + i * 30} ${18 - i * 6})`}>
        <rect width={64} height={72} fill="var(--pal-panel)" stroke={A} strokeOpacity={0.4 + i * 0.3} strokeWidth={1.5} />
        {[14, 24, 34, 44].map(y => <line key={y} x1={10} x2={y === 44 ? 34 : 54} y1={y} y2={y} stroke={A} strokeOpacity={0.3 + i * 0.25} strokeWidth={2} />)}
        {i === 2 && <polyline points="10,62 22,54 32,58 46,48 54,50" fill="none" stroke={A} strokeWidth={2} />}
      </g>
    ))}
  </svg>
);

/** Cohort bars, top vs bottom: the mentor room */
const CohortArt = (
  <svg viewBox="0 0 280 96" width="100%" height="100%" style={{ opacity: 0.55 }}>
    {[70, 62, 55, 48, 40, 34, 26, 20, 14, 10].map((h, i) => (
      <rect key={i} x={40 + i * 21} y={84 - h} width={14} height={h} fill={i < 3 ? A : i > 6 ? LOSS : DIM} opacity={i < 3 || i > 6 ? 0.9 : 0.4} />
    ))}
  </svg>
);

// Resources first (the map to everything), then the journey: readiness → prescription → live → scaling
const SECTIONS: Section[] = [
  { key: 'resources', title: ['', 'Resources'], blurb: 'Discord, Whop, the course, playbooks: everywhere the community lives.', icon: <BookOpen size={22} />, art: PagesArt },
  { key: 'readiness', title: ['Readiness', 'Progression'], blurb: 'Replay and paper trades toward 30-day / 30-trade milestones. Results feed your prescription.', icon: <Hourglass size={22} />, art: LadderArt },
  { key: 'rx', title: ['Prescription', 'Engine'], blurb: 'Your risk numbers, fitted to your firm and your stats.', icon: <ScrollText size={22} />, art: GaugeArt, to: '/assess' },
  { key: 'tracker', title: ['Live', 'Trading'], blurb: 'Your live trade log, strategy by strategy.', icon: <Swords size={22} />, art: CandleArt },
  { key: 'marathon', title: ['Marathon', 'Method'], blurb: 'Rotate your prop-firm cohorts: who takes the next trade, sizing, payouts and the MOAT.', icon: <Shield size={22} />, art: RotationArt, innerCircle: true },
  { key: 'mentor', title: ['Mentor', 'Room'], blurb: 'Cohort stats, trader audits, top-vs-bottom expectancy.', icon: <Crown size={22} />, art: CohortArt, locked: 'Mentors only' },
];

/* Pixel skin: a cut-out pixel-art object per row, resting on the stone */
/* Pixel skin cards are cracked stone tablets, each its own slab, darkened so the copy stays readable */
const stone = (key: string) =>
  `linear-gradient(180deg, rgba(6,22,28,0.3), rgba(3,10,15,0.55)), url(/assets/pixel/slab-${key}.webp) center / cover no-repeat`;

const PixelArt = ({ name }: { name: string }) => (
  <img src={`/assets/pixel/row-${name}.webp`} alt="" draggable={false} style={{
    position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', height: 118, width: 'auto',
    imageRendering: 'pixelated', filter: 'drop-shadow(0 4px 0 rgba(0,0,0,0.45))',
  }} />
);

function Row({ s, open, onToggle, username, strategies, onGateChange }: { s: Section; open: boolean; onToggle: () => void; username: string; strategies: string[]; onGateChange: () => void }) {
  const navigate = useNavigate();
  const { skin } = useSkin();
  const pixel = skin === 'arcade';
  const ref = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<'log' | 'stats'>('log');
  const onViewChange = useCallback((v: 'log' | 'stats') => setView(v), []);
  useEffect(() => { if (open) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [open]);
  // Keep the body mounted through the closing slide, then drop it
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) { setMounted(true); return; }
    const t = setTimeout(() => setMounted(false), 460);
    return () => clearTimeout(t);
  }, [open]);
  const locked = Boolean(s.locked);
  const last = (s.key === 'tracker' || s.key === 'readiness') && view === 'stats' ? 'Stats' : s.title[1];

  return (
    <div ref={ref} style={{ background: pixel ? stone(s.key) : PANEL_GLASS, boxShadow: pixelFrame(open), borderRadius: 'var(--pal-radius, 0)', overflow: 'hidden', opacity: locked ? 0.6 : 1, scrollMarginTop: 16, transition: 'box-shadow 0.3s' }}>
      <button type="button" onClick={locked ? undefined : onToggle} aria-expanded={locked ? undefined : open} disabled={locked}
        className="pal-row-head"
        style={{ width: '100%', display: 'grid', gridTemplateColumns: pixel ? '1fr minmax(0, 300px)' : '56px 1fr minmax(0, 300px)', alignItems: 'center', gap: 20, padding: '18px 22px', background: 'none', border: 'none', cursor: locked ? 'not-allowed' : 'pointer', textAlign: 'left', color: ICE }}>
        {/* The pixel skin's pictures already say what each row is, so it drops the icon badge */}
        {!pixel && <span style={{ width: 52, height: 52, display: 'grid', placeItems: 'center', background: FIELD, color: open ? ACCENT : ICE, borderRadius: 'var(--pal-radius, 0)', boxShadow: 'var(--pal-field-shadow)' }}>{s.icon}</span>}
        <span>
          <span className="pal-title" style={{ display: 'block', fontFamily: TITLE_FONT, fontWeight: 900, fontSize: 30, lineHeight: 1.1 }}>
            {s.title[0] && <>{s.title[0]} </>}<SwapWord className="pal-em" word={last} />
          </span>
          <span style={{ display: 'block', fontFamily: PX_FONT, fontSize: 13, color: TEAL, marginTop: 6 }}>
            {locked ? <><Lock size={11} style={{ display: 'inline', verticalAlign: -1 }} /> {s.locked} · </> : null}{s.blurb}
          </span>
        </span>
        <span style={{ height: 96, position: 'relative' }}>
          <span style={{ position: 'absolute', inset: 0, transition: 'opacity 0.3s, transform 0.3s', opacity: open ? 0 : 1, transform: open ? 'translateX(20px)' : 'none' }}>
            {pixel ? <PixelArt name={s.key} /> : s.art}
          </span>
          <span style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', transition: 'opacity 0.3s', opacity: open ? 1 : 0, display: 'flex', alignItems: 'center', gap: 6, fontFamily: PX_FONT, fontSize: 12, color: TEAL }}>
            Close <X size={18} />
          </span>
        </span>
      </button>

      {/* Slide-open body: grid rows 0fr → 1fr animates to the content's natural height */}
      <div style={{ display: 'grid', gridTemplateRows: open && mounted ? '1fr' : '0fr', transition: 'grid-template-rows 0.45s cubic-bezier(0.22, 1, 0.36, 1)' }}>
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          {mounted && (
            <div style={{ padding: '6px 22px 26px', borderTop: '1px solid var(--pal-grid)' }}>
              {s.key === 'readiness' && pixel ? (
                <ReadinessBook />
              ) : s.key === 'tracker' || s.key === 'readiness' ? (
                <div style={{ paddingTop: 20 }}><Tracker embedded track={s.key === 'readiness' ? 'readiness' : 'live'} onViewChange={onViewChange} /></div>
              ) : s.key === 'rx' ? (
                <div style={{ paddingTop: 20 }}><PrescriptionEngine username={username} onChosen={onGateChange} /></div>
              ) : s.key === 'resources' ? (
                pixel ? <ResourcesBook /> : <ResourceGrid />
              ) : s.key === 'marathon' ? (
                <div style={{ paddingTop: 20 }}><MarathonView username={username} strategies={strategies} /></div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, paddingTop: 18, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: PX_FONT, fontSize: 13, color: TEAL }}>This section still opens on its own page while it's brought into Home Base.</span>
                  <button type="button" onClick={() => s.to && navigate(s.to)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', border: 'none', cursor: 'pointer', background: ACCENT, color: 'var(--pal-on-accent)', fontFamily: PX_FONT, fontWeight: 700, fontSize: 13, borderRadius: 'var(--pal-radius, 0)' }}>
                    Open {s.title.join(' ').trim()} <ArrowRight size={14} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function HomeRows() {
  const { user } = useAppState();
  const { skin } = useSkin();
  const username = user?.discordUsername || 'trader';
  const [roles, setRoles] = useState<string[] | null>(null);
  const [strategies, setStrategies] = useState<string[]>([]);
  useEffect(() => {
    authApi.me(username).then(me => setRoles(me.roles)).catch(() => setRoles([]));
    strategiesApi.list(username).then(list => setStrategies(list.map(st => st.code))).catch(() => setStrategies([]));
  }, [username]);
  const innerCircle = roles?.includes('inner_circle') ?? false;
  // Readiness → Prescription Engine (opens when a strategy finishes paper) → Live Trading (opens when a tier is locked in)
  const [engineOpen, setEngineOpen] = useState<boolean | null>(null);
  const [liveOpen, setLiveOpen] = useState<boolean | null>(null);
  const refreshGates = useCallback(() => {
    // Fast-tracked members (mods, verified veterans, an existing live record) start with everything open
    Promise.all([tradesApi.readiness(username), engineApi.choices(username)]).then(([r, c]) => {
      const all = Object.values(r.strategies);
      setEngineOpen(all.some(s => s.ready));
      setLiveOpen(c.length > 0 || all.some(s => s.fastTrack !== null));
    }).catch(() => { setEngineOpen(null); setLiveOpen(null); });
  }, [username]);
  useEffect(() => { refreshGates(); }, [refreshGates]);

  // Any number of rows can be open; ?open=tracker,marathon
  const [params, setParams] = useSearchParams();
  const openKeys = (params.get('open') ?? '').split(',').filter(Boolean);
  const toggle = (key: string) => {
    const keys = openKeys.includes(key) ? openKeys.filter(k => k !== key) : [...openKeys, key];
    const next = new URLSearchParams(params);
    if (keys.length) next.set('open', keys.join(',')); else next.delete('open');
    setParams(next);
  };
  const lockOf = (s: Section) => (s.innerCircle && roles && !innerCircle ? 'Inner Circle only'
    : s.key === 'rx' && engineOpen === false ? 'Opens when you finish paper trading in Readiness Progression'
    : s.key === 'tracker' && liveOpen === false ? 'Opens once you lock in a risk tier in the Prescription Engine' : s.locked);

  // Pixel skin: the areas are books on a library shelf
  if (skin === 'arcade') {
    // Areas without their own book yet open the classic panel on a parchment sheet
    const sheet = (body: ReactNode) => (closed: boolean) => (
      <div className="sb-scroll" style={{ width: 'min(1040px, 92vw)', maxHeight: '82vh', overflowY: 'auto', padding: '24px 28px', background: '#0A1E26',
        boxShadow: '0 0 0 3px #1A0E10, 0 0 0 9px #4E2E1A, 0 0 0 12px #1A0E10', opacity: closed ? 0 : 1, transition: 'opacity 0.5s' }}>{body}</div>
    );
    // Stack order on the desk, each column bottom → top: left Live Trading, Readiness, Resources;
    // right Mentor, Marathon, Prescription
    const DESK = ['tracker', 'readiness', 'resources', 'mentor', 'marathon', 'rx'];
    const onDesk = [...SECTIONS].sort((a, b) => DESK.indexOf(a.key) - DESK.indexOf(b.key));
    const books: ShelfBook[] = onDesk.map(s => ({
      key: s.key, title: SPINE[s.key] ?? s.title.filter(Boolean).join(' '), color: BINDING[s.key] ?? 'brown', locked: lockOf(s),
      render: s.key === 'resources' ? closed => <ResourcesBook closed={closed} />
        : s.key === 'readiness' ? closed => <ReadinessBook closed={closed} />
        : s.key === 'rx' ? closed => <PrescriptionBook closed={closed} onChosen={refreshGates} />
        : s.key === 'tracker' ? sheet(<Tracker embedded track="live" />)
        : s.key === 'marathon' ? sheet(<MarathonView username={username} strategies={strategies} />)
        : sheet(<p style={{ fontFamily: PX_FONT, color: TEAL }}>The Mentor Room is being written.</p>),
    }));
    return <Library books={books} who={username} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {SECTIONS.map(s => {
        // Readiness → Prescription Engine (opens when paper trading is complete) → Live Trading
        const section = { ...s, locked: lockOf(s) };
        return <Row key={s.key} s={section} open={!section.locked && openKeys.includes(s.key)} onToggle={() => toggle(s.key)} username={username} strategies={strategies} onGateChange={refreshGates} />;
      })}
    </div>
  );
}
